import { ReactNode, useId, useRef, useState } from 'react';
import { Combobox, Input, useCombobox } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import { useUncontrolled } from '@mantine/hooks';
import clsx from 'clsx';
import dayjs from 'dayjs';

import { useFormattingLocale } from '../../utils/getFormattingLocales';
import { FormLabel } from '../formlabel/FormLabel';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';

import { ensureDayjsLocales } from './helper/ensureDayjsLocales';
import {
  dateToMantineDateString,
  mantineDateStringToDate,
} from './helper/mantineDateStrings';
import { useIsDayExcluded } from './helper/useIsDayExcluded';
import { DateInput } from './DateInput';

import calendarStyles from './calendar.module.scss';
import styles from './date-picker.module.scss';

ensureDayjsLocales();
export type SingleDatePickerProps = {
  availableDates?: Date[];
  dateInputClassName?: string;
  dateTimeFormatOptions?: Intl.DateTimeFormatOptions;
  defaultValue?: Date | null;
  description?: ReactNode | ReactNode[];
  disabled?: boolean;
  readOnly?: boolean;
  clearable?: boolean;
  icon?: string;
  label?: ReactNode | ReactNode[];
  loading?: boolean;
  onChange: (newValue: Date | null) => void;
  placeholder: string;
  value?: Date | null;
  withinPortal?: boolean;
  minDate?: Date;
  maxDate?: Date;
};

export function SingleDatePicker({
  availableDates,
  clearable = true,
  dateInputClassName,
  dateTimeFormatOptions,
  defaultValue,
  description,
  disabled,
  readOnly = false,
  icon = 'event',
  label,
  onChange,
  loading,
  placeholder,
  value,
  withinPortal = true,
  minDate,
  maxDate,
}: SingleDatePickerProps) {
  const id = useId();
  const locale = useFormattingLocale();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isDayExcluded = useIsDayExcluded(availableDates);
  // select first available date per default or fallback to today
  const [currentDate, setCurrentDate] = useState(
    availableDates && availableDates.length > 0 ? availableDates[0] : new Date()
  );
  // note, we use null here so we can differentiate between empty controlled or unset variable
  const [selection, setSelection] = useUncontrolled<Date | null>({
    value,
    defaultValue,
    onChange,
  });
  const combobox = useCombobox({
    onDropdownClose: () => {
      inputRef.current?.blur();
    },
  });

  return (
    <Combobox
      data-testid="date-picker"
      disabled={disabled || loading || readOnly}
      store={combobox}
      withinPortal={withinPortal}
      classNames={{
        dropdown: styles['dropdown'],
        options: styles['options'],
        header: styles['dropdown-header'],
      }}
    >
      <Combobox.EventsTarget>
        <Input.Wrapper
          id={id}
          classNames={{
            root: styles['root'],
            label: clsx(styles['label'], {
              [styles['label--disabled']]: disabled,
            }),
          }}
          onBlur={() => combobox.closeDropdown()}
        >
          <FormLabel
            description={description}
            id={id}
            disabled={disabled}
            loading={loading}
            label={label}
          />
          <Combobox.DropdownTarget>
            <DateInput
              className={dateInputClassName}
              ref={inputRef}
              clearable={clearable}
              data-testid="date-picker"
              loading={loading}
              disabled={disabled === true}
              readOnly={readOnly}
              icon={icon}
              focused={combobox.dropdownOpened}
              value={selection}
              placeholder={placeholder}
              dateTimeFormatOptions={dateTimeFormatOptions}
              onChange={(date) => {
                setCurrentDate(date);

                const isDateOutOfBound =
                  (minDate && date.getTime() < minDate.getTime()) ||
                  (maxDate && date.getTime() > maxDate.getTime());

                if (!isDayExcluded(date) && !isDateOutOfBound) {
                  setSelection(date);
                }
              }}
              onClear={() => setSelection(null)}
              onFocus={() => {
                if (selection) {
                  setCurrentDate(selection);
                }
                combobox.openDropdown();
              }}
            />
          </Combobox.DropdownTarget>
        </Input.Wrapper>
      </Combobox.EventsTarget>
      <Combobox.Dropdown>
        <Combobox.Options>
          <GoogleFontIcon
            name="arrow_drop_up"
            solid
            data-position="start"
            className={styles['dropdown-arrow']}
          />
          <Calendar
            locale={locale}
            date={dateToMantineDateString(currentDate)}
            onDateChange={(date) =>
              setCurrentDate(mantineDateStringToDate(date) ?? currentDate)
            }
            excludeDate={isDayExcluded}
            minDate={minDate ? dateToMantineDateString(minDate) : undefined}
            maxDate={maxDate ? dateToMantineDateString(maxDate) : undefined}
            classNames={{
              calendarHeaderControl: calendarStyles['headerControl'],
              calendarHeaderLevel: calendarStyles['headerLevel'],
              day: calendarStyles['day'],
              monthsListCell: calendarStyles['month'],
              yearsListCell: calendarStyles['year'],
            }}
            getDayProps={(dateString) => {
              const date = mantineDateStringToDate(dateString);
              if (!date) {
                return {};
              }

              const selected = selection
                ? dayjs(date).isSame(selection, 'date')
                : false;
              return {
                selected,
                onClick: () => {
                  setSelection(date);
                  combobox.closeDropdown();
                },
              };
            }}
          />
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
